import { Component, OnInit, OnDestroy } from '@angular/core';
import { Location } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router, NavigationEnd } from "@angular/router";
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { environment } from '../../environments/environment';

/**
 * LegacyAppComponent class represents the legacy application component.
 *
 * This component is responsible for displaying the legacy application within the Angular app.
 * It sanitizes the legacy application URL using DomSanitizer to prevent potential security risks.
 * It also handles the redirection within the legacy application by listening to messages from the window.
 */
@Component({
  selector: 'app-legacy-app',
  templateUrl: './legacy-app.component.html',
  styleUrls: ['./legacy-app.component.css'],
  standalone: false
})

export class LegacyAppComponent implements OnInit, OnDestroy {
  legacyAppUrl: string;
  safeLegacyAppUrl!: SafeResourceUrl;
  private routeSubscription!: Subscription;

  constructor(private sanitizer: DomSanitizer, private location: Location, private router: Router) {
    this.legacyAppUrl = environment.legacyAppUrl;
    this.updateIframeSrc();
  }

  private updateIframeSrc(): void {
    this.safeLegacyAppUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.legacyAppUrl + this.router.url);
  }

  /**
   * The ngOnInit method is a lifecycle hook that is executed after the component has been initialized.
   *
   * This method adds an event listener to the window object for the 'message' event. When the 'message' event is triggered,
   * it checks if the event originated from the same URL as the legacyAppUrl property. If the origin matches, it further checks
   * if the data received has the kind property set to 'url_data'. If it matches, it extracts the address from the data and
   * replaces the current URL state with the extracted address.
   *
   * It also subscribes to router navigation events to update the iframe URL when the route changes.
   *
   * @returns {void}
   */
  ngOnInit(): void {
    window.addEventListener('message', (event: MessageEvent) => {
      if (event.origin === this.legacyAppUrl) {
        if(event.data.kind === 'url_data') {
          const address = event.data.text.toString().replace(this.legacyAppUrl, '');
          this.location.replaceState(address);
        }
      }
    });

    // Subscribe to route changes to update the iframe URL
    this.routeSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateIframeSrc();
      });
  }

  ngOnDestroy(): void {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
  }
}
